import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase";
import { api } from "../api/api";
// Sola grafia (spazi e maiuscole), nessuna traduzione: qui si leggono
// status, non ruoli. La sede della normalizzazione e' una: auth/access.js.
import { normalizeRole } from "../auth/access";
// IL METRO DELL'ANNO, dalla sua sede unica (cantiere «lettore unico»). E' il
// corpo che stava qui sotto come `annoDiStagione`, invariato.
// ⚠️ `utils/stagione.js` NON IMPORTA NULLA, e deve restare cosi': e' questo
// file — il codice d'ingresso — a importare lui, e un import di ritorno
// aprirebbe un ciclo proprio sull'avvio dell'app.
import { annoDiLista } from "../utils/stagione";

const AuthContext = createContext(null);

// Quante volte si rilegge la scheda dal backend prima di arrendersi, e quanto
// si attende fra un tentativo e l'altro.
//
// ⚠️ Il tetto e' nel `for` piu' sotto: tre tentativi, due attese, circa un
// secondo e mezzo nel caso peggiore. Chi ha la scheda esce al primo giro, e per
// lui non cambia nulla.
const TENTATIVI_SCHEDA = 3;
const ATTESA_FRA_TENTATIVI_MS = 800;

// QUANTO SI ASPETTA LA STAGIONE, AL MASSIMO, PRIMA DI SBLOCCARE LO STESSO.
//
// ⚠️ E' il tetto che rende SICURO il fatto che da YS-5bis la stagione preceda
// lo sblocco dell'app: `api.get` non ha un timeout proprio, quindi una
// `GET /season-config` che non risponde mai resterebbe appesa per sempre, e
// senza tetto l'app resterebbe sulla schermata bianca. Allo scadere si sblocca
// comunque, con la stagione dichiarata "error" (v. l'attesa nell'osservatore).
//
// Il valore e' l'ordine di grandezza che il file gia' tollera: la lettura
// della scheda, nel caso peggiore, attende 2 x 800 ms fra i suoi tre
// tentativi. Aspettare la stagione al massimo altrettanto non allunga l'avvio
// oltre quello che l'avvio peggiore gia' costa oggi — e nel caso normale non
// lo allunga affatto, perche' il conto parte INSIEME alla lettura della scheda
// e non dopo.
const TETTO_STAGIONE_MS = 1600;

const attendi = (ms) => new Promise((risolvi) => setTimeout(risolvi, ms));

// L'email della richiesta corrisponde a PIU' DI UNA scheda?
//
// RL-8a ha dato al backend un modo di dirlo: `GET /users?uid=` risponde 409 col
// corpo `{ "error": "EMAIL_AMBIGUA" }` quando l'email verificata dell'utenza
// Firebase pesca due o piu' documenti. Qui lo si riconosce SENZA leggere il
// messaggio dell'errore a tentoni: `api.js` (erroreDiRisposta) appende
// all'Error due proprieta' proprio per questo — `status`, il codice HTTP, e
// `body`, il corpo grezzo della risposta.
//
// ⚠️ Servono ENTRAMBE le condizioni. Il solo 409 non basta: e' un codice che
// altre rotte usano per altri conflitti, e trattarlo come «email ambigua»
// mostrerebbe alla persona un messaggio sbagliato. Il corpo si legge dentro un
// try: non e' garantito che sia JSON, e un corpo illeggibile non deve
// trasformare un guasto in un'altra cosa — resta un guasto.
function eEmailAmbigua(err) {
  if (!err || err.status !== 409 || typeof err.body !== "string") return false;

  try {
    return JSON.parse(err.body)?.error === "EMAIL_AMBIGUA";
  } catch {
    return false;
  }
}

// Legge la scheda del backend e dice CON QUALE ESITO, che non e' la stessa cosa
// del dire se la scheda c'e':
//
//   "trovata"  la scheda esiste
//   "assente"  il backend ha risposto, e la scheda non c'e' (200 con lista vuota)
//   "ambigua"  il backend ha risposto 409 EMAIL_AMBIGUA: di schede ce n'e' piu'
//              d'una per questa email, e non tocca al frontend sceglierne una
//   "guasto"   la richiesta e' fallita: non sappiamo se la scheda esista
//
// ⚠️ Prima questa distinzione non esisteva: il `catch` scriveva un avviso in
// console e proseguiva con `backendUser` nullo, cioe' trattava «non sono
// riuscito a chiedere» come «la persona non esiste». Bastava un guasto di rete
// o un riavvio del backend per disconnettere chiunque, SuperAdmin compresi.
async function leggiSchedaBackend(uid) {
  try {
    const risposta = await api.get(`/users?uid=${uid}`);

    const scheda = Array.isArray(risposta)
      ? risposta[0] || null
      : risposta || null;

    return { esito: scheda ? "trovata" : "assente", scheda };
  } catch (err) {
    // ⚠️ Nessun dettaglio dell'errore nei log: il messaggio di `api.get`
    // contiene il corpo della risposta, e questa e' l'anagrafica di un club
    // con dei minori. Il fatto che la lettura sia fallita basta.
    console.warn("Backend user not available");
    return {
      esito: eEmailAmbigua(err) ? "ambigua" : "guasto",
      scheda: null
    };
  }
}

// L'anno come intero, oppure `null` se il valore non e' un anno:
// `annoDiLista`, importata in testa al file.
//
// ⚠️ `GET /season-config` restituisce i due anni GREZZI dal documento
// (`configStagioneInRisposta`, server.js): il documento puo'
// portare la grafia in cifre che arriva da un campo di modulo («2027»)
// invece del numero, e chi confronta annate deve confrontare NUMERI — due
// grafie dello stesso anno non si riconoscerebbero fra loro. Qui si
// normalizza perche' la sede condivisa esponga interi e non valori grezzi.
//
// ⚠️ IL CONTO E' CHIUSO. Qui viveva `annoDiStagione`, e il commento diceva:
// «Le due copie convivono per decisione: UsersList tiene la propria lettura
// locale e questa fetta non la tocca. E' la migrazione di quella pagina —
// coda futura, a diario — a chiudere il conto». Quella migrazione e' il
// cantiere «lettore unico»: le SETTE copie identiche (questa, UsersList,
// TeamAssignment, UserContextManager, HomeParent, HomePlayer,
// MemberProfileModal) sono diventate una, in `utils/stagione.js`, col corpo
// che stava qui — invariato.

// IL CANCELLO DELLA STAGIONE (YS-4): quali tessere valgono PER L'ANNO
// OPERATIVO, e quali no.
//
// Le due sedi che compongono `user.contexts` — l'ingresso e
// `refreshUserFromBackend` — filtrano gia' le tessere a ACTIVE. Questa
// funzione aggiunge il secondo metro, l'ANNO: una tessera ACTIVE di un anno
// che non e' quello operativo non entra in `user.contexts`, quindi non da'
// poteri a valle. Sta qui, in una sede sola, perche' i due punti applichino
// LA STESSA regola: due copie divergerebbero al primo ritocco.
//
// ⚠️ CIO' CHE ARRIVA E' GIA' FILTRATO A ACTIVE. Questa funzione non guarda
// lo `status` e non deve farlo: i chiamanti le passano tessere gia' attive
// (e sempre un array). Il cancello e' «solo anno», sopra un cancello «solo
// stato» che resta dov'era.
//
// ⚠️ E' UN CANCELLO SOLO-TESSERE. `getActiveRoles` (access.js) fa
// `add(user.role)`: il ruolo di PRIMO LIVELLO non passa di qui. Chiuderlo
// non e' compito di YS-4 — dichiararlo si'.
const tessereDellAnnoOperativo = (contexts, stagione) => {
  const { seasonStatus, operatingSeason } = stagione || {};

  // GUARDIA SUL NULL — se la stagione non c'e', NON SI FILTRA.
  //
  // Il caso peggiore della stagione dev'essere «nessun cancello», mai «app
  // rotta»: se `GET /season-config` e' andata storta, o il tetto e' scaduto
  // (`seasonStatus:"error"`), togliere le tessere a tutti sarebbe una
  // serrata del club decisa da un guasto di rete. Si degrada al filtro di
  // prima, quello a ACTIVE, che ha gia' fatto il suo lavoro a monte.
  //
  // ⚠️ Le due condizioni sono ridondanti OGGI — nei tre stati un
  // `operatingSeason` valorizzato implica `seasonStatus:"ready"` — e la
  // ridondanza e' voluta: nessuna delle due da' sola la garanzia se un
  // domani gli stati diventano quattro. `== null` (non `===`) prende null e
  // undefined insieme.
  if (seasonStatus !== "ready" || operatingSeason == null) return contexts;

  return contexts.filter(ctx => {
    const grezzo = ctx?.cardSeason;

    // TESSERA SENZA ANNO: PASSA. Ramo ESPLICITO, non un confronto secco.
    //
    // `annoDiLista(undefined)` vale `null`, e `null === 2026` e' falso:
    // un confronto secco scarterebbe tutte le tessere prive di `cardSeason`.
    // Sono due gruppi che non si possono perdere: le tessere STORICHE (che
    // l'anno non l'hanno mai avuto, in attesa della migrazione YS-N) e
    // soprattutto quelle AMMINISTRATIVE — ADMIN e SUPERADMIN non portano
    // `cardSeason` e non possono portarlo (server.js: RUOLI_REGISTRABILI,
    // RUOLI_AMMESSI_NEI_CONTEXTS). Senza questo ramo, il primo effetto
    // del cancello sarebbe togliere i poteri agli amministratori.
    const senzaAnno =
      grezzo === undefined ||
      grezzo === null ||
      (typeof grezzo === "string" && grezzo.trim() === "");

    if (senzaAnno) return true;

    // L'anno C'E', e allora deve essere quello operativo. Confronto fra
    // INTERI, con lo stesso metro che ha normalizzato `operatingSeason`
    // (`annoDiLista`, dal modulo): «2026» e 2026 sono lo stesso anno, e
    // devono riconoscersi. Una grafia illeggibile («abc», un oggetto) da'
    // `null`, non combacia, e la tessera viene scartata: e' il verso sicuro
    // — un anno scritto storto non concede nulla.
    return annoDiLista(grezzo) === operatingSeason;
  });
};

// IL COMPLETAMENTO DELLA STAGIONE (RENEW-COMPLETAMENTO): questa persona ha
// ancora da confermare i propri dati PER L'ANNO DI ENROLLMENT?
//
// Un SI'/NO calcolato qui, e non un secondo array: il gate (RequireRole)
// legge il booleano e non ragiona mai sulle tessere. Sta ACCANTO al cancello
// qui sopra, non dentro: `user.contexts` resta esattamente quello che era —
// le tessere ammesse — e nessuno dei suoi consumatori (ruoli, permessi,
// cappello, squadre, destinatari dei messaggi) cambia di una virgola.
//
// ⚠️ L'ANNO E' QUELLO DI ENROLLMENT, NON L'OPERATIVO. Sono due anni diversi
// in fase di sovrapposizione (enrollment = operativo + 1), ed e' esattamente
// la fase in cui vive il rinnovo: una domanda posta sull'anno operativo non
// fermerebbe nessun rinnovante.
//
// ⚠️ SI CALCOLA DALLE TESSERE GIA' FILTRATE A ACTIVE, ed e' voluto: il
// completamento scatta DOPO l'Approve, e una tessera ancora PENDING ha la
// sua vista d'attesa, che nel cancello dei ruoli viene prima di questa.
//
// ⚠️ TESSERA SENZA ANNO: NON CONTA — il verso OPPOSTO a quello del cancello
// qui sopra, e per una ragione precisa. La', chi non ha `cardSeason` (le
// tessere amministrative, le storiche) deve conservare i propri poteri; qui
// la domanda e' «ce n'e' una DI QUELL'ANNO da confermare?», e una tessera
// senza anno non e' di quell'anno. Sommato all'ESISTENZIALE (`some`, che
// sull'insieme vuoto e' FALSO), questo fa si' che ADMIN, SUPERADMIN, tessere
// storiche e chi non ha rinnovato non finiscano mai al completamento.
//
// ⚠️ CONFERMATO SOLO SE `seasonConfirmed === true`: il campo ASSENTE non e'
// una conferma. E' il metro del cantiere, e le tessere gia' a documento —
// nate prima della fetta backend — quel campo non ce l'hanno.
//
// GUARDIA SULLA STAGIONE: se non e' "ready" l'anno di enrollment non si sa,
// e si risponde `false`. Stesso verso del cancello qui sopra e di
// `STAGIONE_NON_ANCORA`: nel dubbio non si dichiara mai una chiusura. Un
// guasto di `GET /season-config` non deve chiudere il club intero dentro la
// finestra di completamento.
//
// ⚠️ IL TERZO ARGOMENTO — LA FAMIGLIA (rilievo R-1). Le tessere che
// arrivano qui sono quelle della SOLA persona collegata: un genitore che ha
// confermato per se' entrerebbe nell'app anche con un figlio ancora da
// confermare. La risposta di FAMIGLIA la da' il backend, che i figli li
// vede: la scrive sul documento del genitore (`seasonConfirmedFamily`, la
// cascata di `confirm-season`), e di qui arriva dallo spread di
// `backendUser`.
//
// ⚠️ TERMINE AGGIUNTIVO, NON SOSTITUTIVO, ed e' una scelta, non un
// ripiego. Il campo di famiglia non prende il posto del calcolo sulle
// tessere proprie perche' nessuno lo ricalcola al cambio d'anno: a muoverlo
// e' solo una conferma. Un `true` rimasto dalla stagione scorsa farebbe
// quindi entrare, a stagione nuova, un genitore con la tessera nuova tutta
// da confermare. Sommato in OR, il primo termine continua a fermarlo.
//
// ⚠️ SOLO UN `false` ESPLICITO TRATTIENE: campo assente — il documento non
// ce l'ha — oppure `true` non aggiungono nulla al conto. E' la stessa
// convenzione della cautela del backend, dove il campo non viene mai
// introdotto dove non c'e', ed e' il verso di sempre: nel dubbio non si
// chiude fuori nessuno. Chi non ha figli quel campo non lo riceve dalla
// cascata: se lo porta, lo scrive il backend calcolandolo sulle sole tessere
// proprie — alla nascita (il costruttore) e a ogni sua conferma
// (`ricalcolaSeasonConfirmedFamily`, ramo del bersaglio); se non lo porta
// (documenti nati prima della semina), risponde sulle proprie tessere come
// prima.
const completamentoStagionaleDaFare = (
  contexts,
  stagione,
  seasonConfirmedFamily
) => {
  const { seasonStatus, enrollmentSeason } = stagione || {};

  if (seasonStatus !== "ready" || enrollmentSeason == null) return false;

  // Il termine di famiglia, DOPO la guardia sulla stagione: senza l'anno di
  // enrollment non si dichiara una chiusura, nemmeno per la famiglia.
  if (seasonConfirmedFamily === false) return true;

  const elenco = Array.isArray(contexts) ? contexts : [];

  return elenco.some(ctx => {
    if (!ctx || typeof ctx !== "object") return false;

    // Stesso metro d'anno del cancello (`annoDiLista`): «2027» e 2027 sono
    // lo stesso anno. Una grafia illeggibile da' `null`, non combacia, e la
    // tessera non entra nel conto.
    if (annoDiLista(ctx.cardSeason) !== enrollmentSeason) return false;

    return ctx.seasonConfirmed !== true;
  });
};

// LO STATO DEL RINNOVO DEL SINGOLO SOCIO PER L'ANNO D'ISCRIZIONE (D-02, R2.2)
// — il «cartellino» che la riga del pannello di rinnovo (EnrolNextSeason.jsx)
// legge per decidere QUALE riga mostrare. Una fra:
//   "nessuna"                nessuna tessera di quell'anno
//   "inAttesa"               tessera PENDING (ha chiesto, il club non ha approvato)
//   "approvatoDaCompletare"  tessera ACTIVE con `seasonConfirmed !== true`
//   "completato"             tessera ACTIVE con `seasonConfirmed === true`
//   "archiviata"             tessera ARCHIVED (respinta o archiviata)
//
// ⚠️ SERVE PERCHE' `renewedSeason` DICE SOLO «HA CHIESTO»: nessun gesto
// successivo lo cambia, e la riga «awaiting approval» restava anche dopo
// l'Approve. La risposta vera sta nella tessera dell'anno.
//
// ⚠️ SI CALCOLA DALLE TESSERE GREZZE, NON DA QUELLE FILTRATE A ACTIVE: al
// contrario di `completamentoStagionaleDaFare`, qui la tessera PENDING e'
// proprio cio' che si cerca, e il filtro a ACTIVE la toglierebbe. Chi chiama
// passa `backendUser.contexts` com'e' arrivato dal server.
//
// ⚠️ PRECEDENZA, con piu' tessere dello stesso anno (es. player + coach):
// inAttesa > approvatoDaCompletare > completato > archiviata. E' l'ordine di
// `statoDelleTessere` (utils/squadre.js): PENDING vince; ARCHIVED solo se lo
// sono TUTTE; altrimenti ACTIVE — e una tessera con stato ignoto o assente
// conta come ACTIVE, come la'. Dentro ACTIVE la distinzione in piu' segue
// `completamentoStagionaleDaFare`: basta UNA tessera da confermare perche'
// resti da completare.
//
// Stessi metri di `completamentoStagionaleDaFare`: anno con `annoDiLista`
// (la tessera senza anno non conta), confermato solo se `=== true`, e la
// stessa guardia sulla stagione — senza anno d'iscrizione si risponde
// "nessuna", cioe' la riga di sempre.
//
// ⚠️ E' SOLO UN CAMPO DA MOSTRARE: non lo legge alcun cancello e non torna
// mai al backend.
const statoRinnovoDellAnno = (contexts, stagione) => {
  const { seasonStatus, enrollmentSeason } = stagione || {};

  if (seasonStatus !== "ready" || enrollmentSeason == null) return "nessuna";

  const stati = (Array.isArray(contexts) ? contexts : [])
    .filter(
      ctx =>
        ctx &&
        typeof ctx === "object" &&
        annoDiLista(ctx.cardSeason) === enrollmentSeason
    )
    .map(ctx => {
      const stato = normalizeRole(ctx.status);
      if (stato === "PENDING") return "inAttesa";
      if (stato === "ARCHIVED") return "archiviata";
      return ctx.seasonConfirmed === true
        ? "completato"
        : "approvatoDaCompletare";
    });

  return stati.length === 0
    ? "nessuna"
    : stati.includes("inAttesa")
    ? "inAttesa"
    : stati.includes("approvatoDaCompletare")
    ? "approvatoDaCompletare"
    : stati.includes("completato")
    ? "completato"
    : "archiviata";
};

// LA STAGIONE NON ANCORA SAPUTA — il primo dei TRE stati.
//
// ⚠️ E' un riferimento stabile di proposito. L'effetto della stagione lo
// rimette quando la sessione si chiude, e confrontarlo per IDENTITA' evita
// di riscrivere lo stato (e quindi di far ridisegnare l'app) quando non
// c'e' nulla da azzerare — al montaggio, per esempio, dove lo stato e' gia'
// questo. Congelato perche' e' condiviso fra tutti i giri e nessuno deve
// modificarlo per sbaglio.
const STAGIONE_NON_ANCORA = Object.freeze({
  seasonStatus: "loading",
  operatingSeason: null,
  enrollmentSeason: null,
  // Il terzo valore (YS-10a). Fuori da "ready" non vale — chi legge deve
  // guardare `seasonStatus` — ma sta a `true` e non a `null` perche' il suo
  // difetto e' «aperte»: nel dubbio non si dichiara mai una chiusura.
  enrollmentsOpen: true
});

// Legge la stagione del club e dice CON QUALE ESITO — stessa dottrina di
// `leggiSchedaBackend` qui sopra: «non sono riuscito a chiedere» non e' «non
// c'e'». I due anni tornano gia' normalizzati a intero.
//
// `GET /season-config` e' protetta dal solo `requireAuth` (server.js):
// non e' un dato riservato, e' il calendario del club. Ma il token lo allega
// `authHeaders` leggendo `auth.currentUser` (api.js), quindi la
// chiamata ha senso SOLO da sessione autenticata: senza utenza Firebase
// parte senza header e il backend risponde 401.
async function leggiStagioneDelClub() {
  try {
    const dati = await api.get("/season-config");

    return {
      esito: "letta",
      operatingSeason: annoDiLista(dati?.operatingSeason),
      enrollmentSeason: annoDiLista(dati?.enrollmentSeason),
      // ⚠️ Stesso metro tollerante del server (`configStagioneInRisposta`):
      // chiuso SOLO se la risposta dice esplicitamente `false`. Un campo
      // mancante — cassettina nata prima di YS-10a — vale «aperte».
      enrollmentsOpen: dati?.enrollmentsOpen === false ? false : true
    };
  } catch {
    // ⚠️ Come per la scheda: nessun dettaglio dell'errore nei log. Il fatto
    // che la lettura sia fallita basta.
    console.warn("Season config not available");
    return {
      esito: "guasto",
      operatingSeason: null,
      enrollmentSeason: null,
      // Gli anni non sappiamo quali siano (`null`), ma le iscrizioni non si
      // dichiarano chiuse per un guasto di lettura: allarmerebbe a vuoto.
      enrollmentsOpen: true
    };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready
  // PERCHE' L'INGRESSO NON E' RIUSCITO — un CODICE, non una frase (RL-8c).
  //
  // Vale null quando non c'e' niente da dire, e altrimenti uno fra:
  //   "not-enrolled"    il backend ha risposto, piu' volte, che scheda non ce
  //                     n'e': questa email non e' iscritta;
  //   "email-ambiguous" 409 EMAIL_AMBIGUA: di schede ce n'e' piu' d'una;
  //   "profile-error"   la lettura e' fallita: la scheda probabilmente c'e',
  //                     ma non siamo riusciti a leggerla.
  //
  // ⚠️ QUI NON CI VANNO I TESTI. Le frasi che l'utente legge stanno tutte in
  // Login.jsx, in un posto solo: questo contesto dice che cos'e' successo, la
  // pagina decide come dirlo. Prende il posto del vecchio `error`, che portava
  // una frase in inglese ed era esportato senza che nessuno lo leggesse — cioe'
  // i tre esiti-problema finivano in un'espulsione muta.
  const [authReason, setAuthReason] = useState(null);
  const [activeContext, setActiveContext] = useState(null);

  // LA STAGIONE DEL CLUB — i due anni e il loro stato, in un valore solo.
  //
  // ⚠️ UN SOLO `useState`, non tre, perche' i tre campi devono muoversi
  // INSIEME: con stati separati esisterebbe un istante in cui lo stato dice
  // gia' "ready" e gli anni sono ancora `null`, cioe' esattamente il caso
  // che i lettori non devono vedere.
  //
  // ⚠️ TRE stati, non due. «Non ancora arrivata» e «non arrivera'» sono due
  // cose diverse, ed e' la stessa distinzione che `UsersList` paga oggi con
  // uno stato apposta (`cassettinaFallita`, UsersList.jsx):
  // senza, il ripiego di ogni lettore scatterebbe per un istante
  // a ogni ingresso. E' anche la dottrina dei quattro esiti di
  // `leggiSchedaBackend`: «non sono riuscito a chiedere» non e'
  // «non c'e'».
  const [season, setSeason] = useState(STAGIONE_NON_ANCORA);

  // La rilettura della scheda con la stagione PASSATA (`rileggiScheda`, piu'
  // sotto), raggiungibile dall'osservatore per la stagione tardiva (D-ADMIN2,
  // RD-156). Si aggiorna a ogni render; il corpo non dipende dal render,
  // perche' la stagione la riceve come argomento.
  const rileggiSchedaRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // ⚠️ IL CAPPELLO NON SOPRAVVIVE ALL'UTENTE (P1, 8/8/2026).
      //
      // `activeContext` e' il «cappello indossato adesso» che da P1 governa
      // le viste personali del calendario (access.js, currentRole). Azzerare
      // `user` senza azzerare lui lascia in memoria il cappello della
      // sessione precedente: un cappello fantasma, appeso a un utente che
      // non c'e' piu'. Va spento in OGNI uscita senza utente valido — qui
      // (nessuna utenza Firebase: logout o sessione scaduta) e negli altri
      // tre rami piu' sotto. `logout()` lo faceva gia' per conto suo, ma non
      // tutte le disconnessioni passano di li'.
      //
      // ⚠️ E QUI `authReason` NON SI TOCCA (RL-8c). Due dei tre rami-problema
      // piu' sotto fanno `signOut`, e il `signOut` fa ripartire questo stesso
      // osservatore proprio di qui, «nessun utente». Se questo ramo azzerasse
      // il motivo, il messaggio appena impostato verrebbe cancellato un istante
      // dopo e a schermo non comparirebbe nulla: sarebbe l'espulsione muta di
      // prima, con in piu' del codice che sembra dire qualcosa. Il motivo si
      // azzera in due soli punti: quando la scheda si trova (ingresso riuscito)
      // e quando la pagina di accesso chiede un nuovo link
      // (`clearAuthReason`).
      if (!firebaseUser) {
        setUser(null);
        setActiveContext(null);
        setStatus("ready");
        return;
      }

      setStatus("loading");

      // LA STAGIONE PARTE ADESSO, IN PARALLELO ALLA SCHEDA (YS-5bis).
      //
      // ⚠️ RIBALTAMENTO DI YS-5, a verbale. YS-5 caricava la stagione DOPO lo
      // sblocco, in un effetto separato, per non allungare la schermata
      // bianca. Ma il cancello della stagione che verra' (YS-4) si chiude
      // all'INGRESSO, e a un cancello che legge `operatingSeason` ancora
      // `null` non resta nulla da filtrare: sarebbe inerte proprio nel
      // momento in cui deve servire. Quindi ora la stagione PRECEDE lo
      // sblocco.
      //
      // ⚠️ E LO SCOPO DI YS-5 RESTA INTATTO, perche' qui non si aspetta
      // niente: la richiesta parte e basta, e il suo giro di rete si consuma
      // nei tempi morti della lettura della scheda qui sotto (che di giri di
      // rete ne fa da uno a tre, con attese in mezzo). L'attesa vera e' molto
      // piu' in basso, subito prima dello sblocco, dove nel caso normale la
      // risposta e' gia' arrivata e il ritardo aggiunto e' zero.
      //
      // ⚠️ IL TETTO E' GIA' QUI DENTRO, non al punto d'attesa: il conto alla
      // rovescia parte INSIEME alla richiesta, non dopo la scheda. Contarlo
      // piu' tardi sommerebbe due attese (scheda + tetto) invece di
      // sovrapporle.
      //
      // ⚠️ Serve solo l'utenza Firebase, che qui c'e' di sicuro (il ramo
      // «nessun utente» e' uscito poche righe sopra): `authHeaders` allega il
      // token leggendo `auth.currentUser`, e senza token il backend
      // risponderebbe 401. Nessun dato della scheda entra in questa lettura,
      // percio' non c'e' motivo di aspettarla.
      //
      // ⚠️ Nessuno dei rami anomali qui sotto aspetta questa promessa, e va
      // bene: `leggiStagioneDelClub` non rigetta mai (catch interno) e
      // `attendi` nemmeno, quindi una promessa lasciata andare non produce
      // rigetti non gestiti.
      //
      // ⚠️ LA LETTURA HA UN NOME (D-ADMIN2, RD-156): se vince il tetto, la
      // risposta tardiva non si butta — la raccoglie il nuovo tentativo in
      // fondo a questo osservatore. Il tetto e la corsa restano quelli di prima.
      const letturaDelClub = leggiStagioneDelClub();
      const promessaStagione = Promise.race([
        letturaDelClub,
        attendi(TETTO_STAGIONE_MS),
      ]);

      // LETTURA DELLA SCHEDA, CON RITENTO.
      //
      // Alla registrazione l'utenza Firebase nasce gia' collegata, quindi
      // questo osservatore parte SUBITO, mentre POST /register sta ancora
      // scrivendo il documento. GET /users?uid= risponde allora con una lista
      // vuota — il caso e' dichiarato nel backend, «chi ha appena creato
      // l'utenza» — e la lettura secca di prima concludeva che la persona non
      // esistesse, disconnettendola a meta' della propria iscrizione. Da li' in
      // avanti niente token, quindi documenti d'identita' e foto non salivano
      // piu': l'iscrizione risultava riuscita e i file restavano a terra.
      //
      // Chi arriva per primo fra le due richieste dipende dalla rete: era un
      // guasto a intermittenza, il tipo che supera i collaudi e colpisce a caso.
      // Rileggere due volte a distanza di poco lo chiude, perche' la scrittura
      // di /register precede la sua stessa risposta.
      let backendUser = null;
      let letturaFallita = false;
      let emailAmbigua = false;

      for (let tentativo = 1; tentativo <= TENTATIVI_SCHEDA; tentativo++) {
        const lettura = await leggiSchedaBackend(firebaseUser.uid);

        if (lettura.esito === "trovata") {
          backendUser = lettura.scheda;
          letturaFallita = false;
          break;
        }

        // ⚠️ L'ESITO "ambigua" NON SI RITENTA. Il ritento esiste per una corsa
        // — la scheda che sta nascendo mentre gia' la si chiede — e per gli
        // intoppi di rete: due cose che passano da sole. Due schede con la
        // stessa email invece restano due anche fra un secondo e mezzo, e
        // ripetere il 409 servirebbe solo a far aspettare la persona prima di
        // dirle la stessa cosa. Si esce subito e decide il ramo dedicato.
        if (lettura.esito === "ambigua") {
          emailAmbigua = true;
          letturaFallita = false;
          break;
        }

        // Conta l'ESITO DELL'ULTIMO tentativo, non la storia: se l'ultima
        // risposta e' valida e vuota, la scheda non c'e' davvero.
        letturaFallita = lettura.esito === "guasto";

        if (tentativo < TENTATIVI_SCHEDA) {
          await attendi(ATTESA_FRA_TENTATIVI_MS);
        }
      }

      // ⚠️ GUARDIA — questa esecuzione e' ancora quella buona?
      //
      // Con il ritento il giro puo' durare un paio di secondi, e in quel tempo
      // lo stato dell'autenticazione puo' cambiare sotto i piedi: la persona
      // preme Logout, oppure la registrazione fallisce e annulla l'utenza appena
      // creata (auth.currentUser.delete(), in Register.jsx). In entrambi i casi
      // parte un nuovo giro di questo stesso osservatore, che azzera l'utente;
      // se il giro vecchio arrivasse in fondo e scrivesse i propri effetti,
      // riporterebbe in vita una sessione che non esiste piu'.
      //
      // Sta PRIMA di ogni scrittura di stato, ramo ARCHIVED compreso: un giro
      // sorpassato non tocca nulla e se ne va in silenzio, lasciando decidere a
      // quello nuovo.
      if (auth.currentUser?.uid !== firebaseUser.uid) return;

      // ⚠️ Status normalizzato: il confronto crudo lasciava passare come
      // attivo un documento scritto "Archived", cioe' riapriva l'app a chi
      // e' uscito dal club. Il backend applica gia' questo metro
      // (server.js). Chi non ha status non e' ARCHIVED e prosegue,
      // com'e' sempre stato.
      if (normalizeRole(backendUser?.status) === "ARCHIVED") {
        setUser({
          firebaseUid: firebaseUser.uid,
          email: firebaseUser.email,
          status: "ARCHIVED",
        });
        // Cappello spento: l'utente archiviato non ne ha uno (vedi sopra).
        setActiveContext(null);
        setStatus("ready");
        return;
      }

      let contexts = [];

      // 1️⃣ Contexts espliciti (TEAMTD, multi-team, ecc.)
      if (Array.isArray(backendUser?.contexts) && backendUser.contexts.length > 0) {
        // ⚠️ Stesso metro del gate unico (access.js) e del backend
        // (server.js): un context salvato "Active" non va perso per
        // una maiuscola, e un elemento nullo non deve far saltare la
        // lettura. Nessun default: status assente NON vale ACTIVE.
        //
        // ⚠️ NON cambia solo QUANTI context sopravvivono: cambia anche
        // QUALE resta in posizione 0, e quello diventa l'activeContext
        // (setActiveContext piu' sotto). Un context "Active" prima
        // scartato puo' quindi presentarsi come il cappello indossato, e
        // le pagine che leggono il solo activeContext per decidere cosa
        // mostrare (CalendarClub, Attendance, Messaging) ne risentono.
        // Nessuna escalation — il cappello viene comunque da un context
        // posseduto e attivo — ma un comando puo' SPARIRE a chi lo aveva,
        // se un altro context gli si piazza davanti.
        //
        // ⚠️ Resta una questione di RESA: Messaging non invia il cappello
        // al backend, che ricava identita' e permessi dal token.
        // Vale anche il verso opposto a quello di sopra: puo' salire in
        // posizione 0 un context piu' privilegiato, che la persona non ha
        // scelto (lo Switch non imposta l'activeContext, vedi access.js).
        // Entrambi i versi sono casi di collaudo.
        contexts = backendUser.contexts.filter(
          c => c && normalizeRole(c.status) === "ACTIVE"
        );
      }

      // 2️⃣ BASE CONTEXT IMPLICITO (principio fondamentale)

      if (!backendUser) {
        // EMAIL AMBIGUA: il backend ha risposto, e ha risposto che di schede
        // per questa email ce n'e' piu' d'una (RL-8a). Non e' un guasto e non
        // e' un'assenza: e' un nodo dell'anagrafica che solo il club puo'
        // sciogliere. Chi ha autenticato NON resta collegato — sarebbe una
        // sessione senza scheda, cioe' senza ruolo e senza permessi — e sulla
        // pagina di accesso trova scritto di rivolgersi al club.
        if (emailAmbigua) {
          await signOut(auth);
          setUser(null);
          // Cappello spento, come in ogni uscita senza utente valido.
          setActiveContext(null);
          setAuthReason("email-ambiguous");
          setStatus("ready");
          return;
        }

        // LETTURA FALLITA fino all'ultimo tentativo: non sappiamo se la scheda
        // esista. Non si disconnette per un guasto nostro — la sessione
        // Firebase resta valida e basta ricaricare quando il backend torna,
        // invece di dover ridigitare la password.
        // L'app comunque non si apre: senza utente il cancello del frontend
        // manda al login, e il backend nega per conto suo a chi non ha scheda.
        if (letturaFallita) {
          console.warn("Scheda utente non leggibile: sessione conservata");
          setUser(null);
          // Cappello spento: senza scheda non sappiamo quali context
          // esistano, e quello vecchio non vale piu' nulla (vedi sopra).
          setActiveContext(null);
          // ⚠️ Nessun `signOut` qui, ed e' proprio questo che rende sensato il
          // «Retry» della pagina di accesso: la sessione Firebase e' viva, un
          // ricaricamento fa ripartire l'osservatore su di essa e la lettura
          // si ritenta senza chiedere un nuovo link.
          setAuthReason("profile-error");
          setStatus("ready");
          return;
        }

        // Il backend ha risposto, piu' volte, che questa scheda non c'e':
        // un'utenza senza anagrafica non resta collegata. E' la difesa di
        // sempre, che il ritento sposta piu' avanti ma non toglie.
        await signOut(auth);
        setUser(null);
        // Cappello spento: questa uscita fa `signOut` per conto proprio e
        // non passa da `logout()`, che l'azzeramento lo faceva gia' (v. sopra).
        setActiveContext(null);
        setAuthReason("not-enrolled");
        setStatus("ready");
        return;
      }

      // ⚠️ QUI, E SOLO QUI, SI ASPETTA LA STAGIONE (YS-5bis).
      //
      // E' l'unico ramo che porta a una sessione USABILE — utente con ruolo e
      // contexts, permessi accesi, app disegnata — cioe' l'unico dove il
      // cancello della stagione avra' qualcosa da filtrare. I cinque rami che
      // sbloccano qui sopra non ci arrivano: «nessuna utenza», ARCHIVED,
      // email ambigua, lettura fallita, non iscritto. Quattro di loro
      // lasciano `user` a null e il quinto (ARCHIVED) e' una schermata di
      // stop senza ruoli: far aspettare LORO la stagione allungherebbe un
      // messaggio d'errore senza cambiare niente di cio' che mostrano.
      //
      // ⚠️ NEL CASO NORMALE QUESTA RIGA NON ASPETTA NULLA. La richiesta e'
      // partita prima della lettura della scheda e ha avuto tutto il tempo
      // della scheda per tornare: la promessa e' gia' risolta e l'`await`
      // passa nel giro di microtask successivo. Il ritardo aggiunto e'
      // `max(0, T_stagione - T_scheda)`, che e' zero ogni volta che la
      // scheda ha dovuto ritentare.
      //
      // ⚠️ E NON ASPETTA MAI PER SEMPRE. La promessa e' gia' corsa col tetto
      // (v. `TETTO_STAGIONE_MS` la' sopra): se `GET /season-config` non
      // risponde — o risponde male — vince il tetto, `letturaStagione` vale
      // `undefined` e si prosegue col ramo "error". L'app SI SBLOCCA
      // COMUNQUE: il caso peggiore della stagione e' «nessun cancello», mai
      // «app bloccata».
      const letturaStagione = await promessaStagione;

      // ⚠️ Guardia di sorpasso, di nuovo: l'`await` qui sopra e' un punto di
      // sospensione nuovo, e nel frattempo la persona puo' essere uscita o
      // essere entrata un'altra. Stessa regola della guardia dopo il ciclo
      // della scheda — un giro sorpassato non scrive nulla.
      if (auth.currentUser?.uid !== firebaseUser.uid) return;

      // La stagione si scrive INSIEME all'utente, non dopo: quando `status`
      // diventa "ready" i due anni sono gia' al loro posto, ed e' esattamente
      // cio' che YS-5 non poteva garantire.
      setSeason(
        letturaStagione?.esito === "letta"
          ? {
              seasonStatus: "ready",
              operatingSeason: letturaStagione.operatingSeason,
              enrollmentSeason: letturaStagione.enrollmentSeason,
              enrollmentsOpen: letturaStagione.enrollmentsOpen
            }
          : {
              // Guasto della lettura OPPURE tetto scaduto: per chi legge sono
              // la stessa cosa — la stagione non c'e' e non arrivera'. Gli
              // anni restano `null`, e la guardia sul null di chi verra' dopo
              // degrada da sola a «non filtro».
              seasonStatus: "error",
              operatingSeason: null,
              enrollmentSeason: null,
              // Il terzo valore non degrada a `null` ma ad «aperte»: un
              // guasto di lettura non deve mai leggersi come una chiusura.
              enrollmentsOpen: true
            }
      );

      // IL CANCELLO DELLA STAGIONE, QUI E NON PIU' SU (YS-4).
      //
      // ⚠️ PERCHE' QUI, e non nel filtro a ACTIVE la' sopra — che era il
      // punto indicato e si e' rivelato inservibile. Le tessere si filtrano
      // a ACTIVE MOLTO prima, ma in quel punto la stagione non
      // esiste ancora, per DUE ragioni indipendenti: `letturaStagione` e'
      // dichiarata piu' in basso (zona morta temporale — leggerla lassu'
      // sarebbe un ReferenceError, non un cancello), e lo stato `season`
      // nemmeno servirebbe, perche' questo osservatore vive in un
      // `useEffect` con dipendenze VUOTE e la sua closure vede per sempre la
      // stagione del primo render, cioe' `STAGIONE_NON_ANCORA`. Un cancello
      // scritto lassu' avrebbe letto `operatingSeason: null` a ogni giro e
      // la guardia sul null l'avrebbe spento SEMPRE: inerte al 100%, che e'
      // il difetto per cui questa fetta era gia' stata rifatta una volta.
      //
      // Qui invece la stagione e' arrivata (`await` poche righe sopra) ed e'
      // la STESSA che sta per essere scritta con `setSeason`: nessuna
      // seconda lettura, quindi nessun modo perche' il cancello e il resto
      // dell'app vedano due anni diversi.
      //
      // ⚠️ Nessuna attesa nuova: si legge una promessa gia' consumata. Lo
      // sblocco di YS-5bis non si allunga di un millisecondo.
      //
      // ⚠️ PORTA ANCHE `enrollmentSeason` (RENEW-COMPLETAMENTO), e il cancello
      // non se ne accorge: destruttura i due valori che gli servono e ignora
      // il terzo. Serve a `completamentoStagionaleDaFare`, che ragiona
      // sull'anno di ENROLLMENT e qui non puo' leggerlo dallo stato `season`
      // (la closure del primo render vede per sempre `STAGIONE_NON_ANCORA`,
      // v. il ragionamento qui sopra): lo prende dalla stessa lettura del
      // giro, cioe' dagli stessi due anni che `setSeason` sta per scrivere.
      const stagioneDiQuestoGiro =
        letturaStagione?.esito === "letta"
          ? {
              seasonStatus: "ready",
              operatingSeason: letturaStagione.operatingSeason,
              enrollmentSeason: letturaStagione.enrollmentSeason
            }
          : {
              seasonStatus: "error",
              operatingSeason: null,
              enrollmentSeason: null
            };

      // ⚠️ GOVERNA ANCHE IL CAPPELLO. `contextsAmmessi` non cambia solo
      // QUANTE tessere sopravvivono: cambia anche QUALE resta in posizione
      // 0, che e' l'`activeContext` iniziale (v. il ragionamento lungo al
      // filtro ACTIVE). Ed e' voluto: un cappello di un anno non operativo
      // sarebbe esattamente il potere che questa fetta deve togliere.
      const contextsAmmessi = tessereDellAnnoOperativo(
        contexts,
        stagioneDiQuestoGiro
      );

      // IL SI'/NO DEL COMPLETAMENTO STAGIONALE, calcolato QUI perche' qui
      // l'array grezzo c'e' ancora: si passa `contexts` (le ACTIVE), non
      // `contextsAmmessi`. Il cancello tiene il solo anno OPERATIVO, e in
      // sovrapposizione la tessera da confermare — che e' di quello di
      // ENROLLMENT — non gli sopravvive: chiederlo dopo darebbe `false` a
      // chiunque, cioe' un gate inerte.
      // ⚠️ IL TERZO ARGOMENTO VIENE DAL DOCUMENTO, non dalle tessere: e' la
      // risposta di famiglia scritta dal backend (rilievo R-1). Si legge da
      // `backendUser` grezzo, prima di qualunque filtro, perche' e' un campo
      // di primo livello e con i contexts non ha niente a che vedere.
      const deveCompletareStagione = completamentoStagionaleDaFare(
        contexts,
        stagioneDiQuestoGiro,
        backendUser.seasonConfirmedFamily
      );

      // IL CARTELLINO DEL RINNOVO (D-02, R2.2): stesso anno d'iscrizione del
      // si'/no qui sopra, ma dalle tessere GREZZE del documento — la PENDING
      // e' proprio cio' che cerca, e `contexts` l'ha gia' tolta.
      const statoRinnovo = statoRinnovoDellAnno(
        backendUser.contexts,
        stagioneDiQuestoGiro
      );

      // Ingresso riuscito: un eventuale motivo di un tentativo precedente non
      // deve restare appeso e ricomparire alla prossima uscita.
      setAuthReason(null);

      setUser({
        firebaseUid: firebaseUser.uid,
        email: firebaseUser.email,
        ...backendUser,
        contexts: contextsAmmessi,
        // ⚠️ DOPO lo spread, come `contexts`, e per la stessa ragione: e' un
        // campo DERIVATO, calcolato qui, e nessuna chiave omonima del
        // documento deve poterlo sovrascrivere. Non torna mai indietro al
        // backend: lo legge il solo gate (access.js).
        //
        // ⚠️ «CALCOLATO QUI» NON VUOL PIU' DIRE «SENZA IL BACKEND» (rilievo
        // R-1). Uno dei suoi termini — la risposta di famiglia,
        // `seasonConfirmedFamily` — dal backend ci viene eccome, ed e' il
        // solo modo di sapere dei figli, che di qui non si vedono. Il campo
        // derivato resta pero' questo, e resta composto qui: quello e' un
        // ingrediente, non il verdetto.
        deveCompletareStagione,
        // Campo derivato anch'esso, DOPO lo spread per la stessa ragione. Lo
        // legge la sola riga del rinnovo (EnrolNextSeason.jsx), nessun cancello.
        statoRinnovo,
      });

      // 🎯 context attivo iniziale
      setActiveContext(contextsAmmessi[0] || null);

      setStatus("ready");

      // LA STAGIONE ARRIVATA TARDI (D-ADMIN2, RD-156).
      //
      // Se ha vinto il tetto (`letturaStagione` vale `undefined`), l'app si e'
      // sbloccata con la stagione "error" — e fin qui nulla cambia. Ma la
      // `GET /season-config` e' ancora in volo: prima la sua risposta non la
      // raccoglieva nessuno, e la stagione restava "error" per tutta la
      // sessione («Season not loaded»). Ora, se arriva ed e' buona, si scrive.
      //
      // ⚠️ SUL GUASTO NON SI SCRIVE NULLA: la dottrina di `refreshSeason`. Lo
      // stato "error" e' gia' al suo posto, e un guasto non ha niente da dire.
      //
      // ⚠️ SI SCRIVE SOLO SOPRA "error": con l'aggiornamento a funzione, lo
      // stato che conta e' quello in coda, non quello di questa closure
      // (congelata al primo render). Se nel frattempo `refreshSeason` ha gia'
      // scritto una stagione, o la sessione si e' chiusa, non si tocca nulla.
      //
      // ⚠️ POI SI RILEGGE LA SCHEDA, e L'ORDINE CONTA: all'ingresso il cancello
      // non ha filtrato (stagione "error" → «nessun cancello») e le tessere
      // vanno rifiltrate per l'anno. La stagione si PASSA alla rilettura invece
      // di farla leggere dallo stato: `refreshUserFromBackend`, vista da qui, e'
      // quella del primo render e leggerebbe `STAGIONE_NON_ANCORA`, cioe' di
      // nuovo nessun cancello. `activeContext` resta com'e' (vincolo 1 della
      // rilettura).
      if (letturaStagione === undefined) {
        letturaDelClub
          .then(async (tardiva) => {
            if (tardiva?.esito !== "letta") return;
            if (auth.currentUser?.uid !== firebaseUser.uid) return;

            const stagione = {
              seasonStatus: "ready",
              operatingSeason: tardiva.operatingSeason,
              enrollmentSeason: tardiva.enrollmentSeason,
              enrollmentsOpen: tardiva.enrollmentsOpen
            };

            setSeason((precedente) =>
              precedente.seasonStatus === "error" ? stagione : precedente
            );

            // Via ref: l'effetto ha dipendenze vuote, e il ref e' il modo
            // dichiarato di raggiungere da qui una funzione del corpo.
            await rileggiSchedaRef.current?.(stagione);
          })
          .catch(() => {
            // Una rilettura fallita tiene cio' che si ha (vincolo 3).
            console.warn("Late season re-read not applied");
          });
      }
    });

    return () => unsubscribe();
  }, []);

  // Chi e' collegato ADESSO, come singolo valore stabile.
  //
  // ⚠️ Serve da dipendenza all'effetto qui sotto, e la dipendenza dev'essere
  // l'UID, non l'oggetto `user`: `refreshUserFromBackend` riscrive `user` a
  // ogni salvataggio (v. piu' sotto), e dipendere dall'oggetto farebbe girare
  // l'azzeramento qui sotto a ogni rilettura della scheda, per un dato che non
  // e' cambiato. L'uid cambia solo quando cambia la persona collegata, che e'
  // esattamente quando la stagione va azzerata.
  const uidCollegato = user?.firebaseUid || null;

  // LA STAGIONE SI SPEGNE CON LA SESSIONE — e SOLO questo (YS-5bis).
  //
  // ⚠️ QUESTO EFFETTO NON LEGGE PIU' LA STAGIONE. In YS-5 la leggeva lui,
  // dopo lo sblocco; da YS-5bis la legge l'osservatore, prima dello sblocco
  // (v. la' sopra). Lasciargli anche la lettura significherebbe DUE
  // `GET /season-config` a ogni ingresso, e — peggio — la seconda potrebbe
  // CONTRADDIRE la prima: se la lettura dell'osservatore riesce e questa
  // fallisce, una stagione che l'app aveva gia' tornerebbe "error" un istante
  // dopo, proprio sotto il cancello che dovrebbe usarla. Meglio una lettura
  // sola, nel punto in cui serve.
  //
  // ⚠️ RESTA il compito che l'osservatore non copre: quando la sessione si
  // chiude, la stagione della persona precedente non deve sopravviverle. Il
  // ricaricamento della pagina non perde nulla — l'osservatore riparte e la
  // rilegge — e `refreshUserFromBackend` non tocca l'uid, quindi non fa
  // scattare niente di qui.
  useEffect(() => {
    if (uidCollegato) return;

    // Nessuna sessione (montaggio, logout, sessione scaduta): si torna a
    // «non ancora saputa». Il confronto per identita' evita la riscrittura
    // inutile quando lo stato e' gia' quello — al montaggio, per esempio.
    setSeason((precedente) =>
      precedente === STAGIONE_NON_ANCORA ? precedente : STAGIONE_NON_ANCORA
    );
  }, [uidCollegato]);

  // ✅ QUESTA È LA PARTE CHE MANCAVA
  const login = (backendUser) => {
    setUser(backendUser);
    setStatus("ready");
  };

  // RILETTURA DELLA PROPRIA SCHEDA, A SESSIONE APERTA (P9, voce 4).
  //
  // ⚠️ Questa funzione NON ESISTEVA, e la sua assenza era un difetto vivo:
  // `UserContextManager` la destruttura da `useAuth()` e la chiama
  // dopo aver salvato i propri contexts. Valendo `undefined`, quella
  // chiamata lanciava un TypeError che il suo `catch` ingoiava
  // scrivendo in console «Context list reload failed after save»: chi
  // modificava i PROPRI contexts non vedeva aggiornarsi il proprio utente, e
  // nessuno glielo diceva. Scrivendola qui, quel punto ricomincia a
  // funzionare senza che quel file venga toccato. (L'audit 11 §9.3 la dava
  // per esistente: correzione a verbale in P9.)
  //
  // Serve a chi ha appena modificato SÉ STESSO — dati anagrafici, foto,
  // contexts — perché ciò che l'app disegna a partire dall'utente collegato
  // smetta di mostrare i dati di prima fino al prossimo login.
  //
  // ⚠️ CHE COSA SI AGGIORNA DAVVERO, verificato sul codice e non dedotto:
  // dell'utente collegato l'app disegna la TESTATA (`UserBar.jsx`, via
  // HomeShell) — nome ed email, e nient'altro — e i RUOLI POSSEDUTI, che
  // `getActiveRoles` (access.js) ricava dal `role` di primo livello PIÙ i
  // ruoli dei contexts ACTIVE, e che governano lo Switch e le home. Di quei
  // due ingredienti questa rilettura può cambiarli entrambi.
  // NON esiste alcun avatar né alcuna sigla di iniziali dell'utente
  // collegato: le tre `AuthAvatar` vive disegnano il profilo aperto
  // (MemberProfileModal), l'interlocutore di una chat (Messaging) e la
  // persona di una riga (UsersList), e nessuna delle tre legge questo
  // contesto. Chi scrive «l'avatar in testata non si aggiorna» — l'audit 11
  // §9.3 lo fa — descrive una testata che non c'è.
  //
  // ⚠️ Gli effetti visibili sono DUE. Il primo è quello di
  // `UserContextManager` (contexts propri → Switch e home). Il secondo si è
  // acceso con la fase 1 del cantiere View Profile: da `/myprofile` un
  // ADMIN/SUPERADMIN vede ora i comandi di gestione sulla propria scheda —
  // il blocco Edit del modale era gated sulla prop `currentUserRole`, che
  // quella rotta non passava (residuo dichiarato di P4), e ora si legge per
  // POSSESSO — quindi da lì si può cambiare il proprio nome, e questa
  // rilettura fa sì che il nome cambi anche in testata. È il giorno che P9
  // aveva messo a verbale scrivendo la funzione in anticipo. Resta fuori la
  // foto dell'utente collegato, che non è disegnata da nessuna parte fuori
  // dal modale, il quale si aggiorna già per conto proprio.
  //
  // TRE VINCOLI, posti dal capomastro e da rispettare alla lettera:
  //
  //   1. ⚠️ `activeContext` NON SI TOCCA. Rileggere non è rientrare:
  //      cambiare il cappello indossato sotto i piedi di chi sta salvando la
  //      propria foto sarebbe un'altra decisione — per giunta con effetti su
  //      ciò che la persona vede e, in Messaging, su ciò che finisce nel
  //      payload. Il cappello lo mette l'osservatore qui sopra all'ingresso
  //      (il PRIMO context ACTIVE) e, da F6, lo rimette lo Switch di
  //      HomeContext quando la persona sceglie un ruolo (`chooseRole`
  //      chiama `setActiveContext`, vedi access.js §`currentRole`): gli
  //      scrittori sono percio' DUE, più `logout` che lo spegne. Il vincolo
  //      non cambia di una virgola — anzi conta di più: questa funzione non
  //      deve diventare il TERZO, perché è l'unica che scriverebbe il
  //      cappello senza che nessuno l'abbia chiesto.
  //   2. ⚠️ `status` NON torna mai a "loading". La pagina è già disegnata:
  //      riportarla in attesa farebbe lampeggiare l'app intera per una
  //      lettura che dura un istante.
  //   3. ⚠️ RILETTURA FALLITA = SI TIENE CIÒ CHE SI HA. Mai `signOut`, mai
  //      `setUser(null)`: il guasto è nostro, non della persona. Si esce
  //      restituendo `null`. È il criterio di `ricaricaProfilo` in
  //      MemberProfileModal e del ramo `letturaFallita` qui sopra — dove il
  //      criterio condiviso è «non si disconnette per un guasto nostro»:
  //      quel ramo un `setUser(null)` lo fa, ma è l'ingresso, e lì non
  //      toglie niente a nessuno perché un utente non c'è ancora. A
  //      sessione aperta toglierebbe di mezzo una persona che sta lavorando.
  //
  // È ADDITIVA: nessun percorso esistente di login o di logout cambia.
  //
  // ⚠️ CHI SI RIDISEGNA quando questa funzione scrive: `user` cambia
  // identità, quindi rigira ogni effetto che dipende dall'OGGETTO utente di
  // questo contesto. Il censimento è stato fatto in P9 e sta per esteso nei
  // rapporti del punto; qui bastano le due voci che contano:
  //   - `useFCMDeviceToken` (App.jsx), l'unica con una conseguenza vera:
  //     dichiarata per esteso là, dove la funzione viene passata;
  //   - `UserContextManager`: chi si toglie da sé l'ultimo context che gli
  //     dava il permesso ora se ne accorge subito, perché il proprio utente
  //     si aggiorna davvero. Prima restava su un utente stantio — non per
  //     scelta, ma perché la chiamata a questa funzione lanciava un
  //     TypeError. È il comportamento giusto, e va provato al collaudo.
  //
  // ⚠️ IL CORPO STA IN `rileggiScheda`, CHE RICEVE LA STAGIONE (D-ADMIN2,
  // RD-156). `refreshUserFromBackend` le passa lo stato `season` del render
  // corrente — per i chiamanti non cambia nulla. L'altro chiamante e' la
  // stagione arrivata tardi, nell'osservatore: la' lo stato visto dalla
  // closure e' quello del primo render, e la stagione giusta si puo' solo
  // passare. Il corpo e' quello di prima, con `stagione` al posto di `season`.
  const refreshUserFromBackend = () => rileggiScheda(season);

  const rileggiScheda = async (stagione) => {
    const utenzaFirebase = auth.currentUser;

    // Nessuna utenza collegata: non c'è niente da rileggere, e non è un
    // guasto (può capitare a un logout appena avvenuto).
    if (!utenzaFirebase) return null;

    const lettura = await leggiSchedaBackend(utenzaFirebase.uid);

    // "guasto" e "assente" si trattano allo stesso modo, ed è voluto. Una
    // scheda che il backend dice di non avere non fa disconnettere nessuno da
    // qui: quella difesa è dell'ingresso, dove si sa di essere all'inizio e
    // si ritenta tre volte. Qui siamo a sessione aperta, dopo un salvataggio
    // riuscito; l'unica risposta prudente è tenersi l'utente che si ha.
    if (lettura.esito !== "trovata") return null;

    // ⚠️ Stessa guardia dell'osservatore: fra la richiesta e la risposta la
    // persona può essere uscita, o essere entrata un'altra. Un giro
    // sorpassato non scrive nulla.
    if (auth.currentUser?.uid !== utenzaFirebase.uid) return null;

    const backendUser = lettura.scheda;

    // Stesso metro dei contexts dell'ingresso (e del backend): ACTIVE in
    // qualunque grafia, elementi nulli scartati, nessun default.
    const contexts = Array.isArray(backendUser.contexts)
      ? backendUser.contexts.filter(
          c => c && normalizeRole(c.status) === "ACTIVE"
        )
      : [];

    // E STESSO CANCELLO DELLA STAGIONE DELL'INGRESSO (YS-4).
    //
    // Se valesse solo all'ingresso, basterebbe salvare i propri contexts per
    // riaversi in mano una tessera di un altro anno fino al ricaricamento
    // della pagina: la regola dev'essere la stessa nei due punti, ed e' la
    // ragione per cui vive in una funzione sola.
    //
    // ⚠️ La stagione arriva come PARAMETRO (`stagione`, D-ADMIN2), non da una
    // lettura propria. Il chiamante pubblico, `refreshUserFromBackend`, passa
    // lo stato `season` del render corrente; l'osservatore d'ingresso passa la
    // stagione arrivata tardi, perche' la' la closure e' congelata al primo
    // render e lo stato non lo vede (v. il commento del cancello lassu').
    // Nessuna richiesta nuova a `/season-config`: una seconda lettura potrebbe
    // fallire e riportare "error" su una stagione che l'app ha gia' valida,
    // proprio sotto il cancello che deve usarla.
    const contextsAmmessi = tessereDellAnnoOperativo(contexts, stagione);

    // E LO STESSO SI'/NO DELL'INGRESSO, dalla stessa sede unica e dalle
    // stesse tessere (le ACTIVE, prima del cancello). La stagione e' il
    // parametro `stagione`, che porta tutti e tre i valori: e' lo stesso
    // punto da cui il cancello qui sopra prende l'anno operativo.
    //
    // ⚠️ E' QUESTO RICALCOLO A CHIUDERE IL CERCHIO dopo il Save. La finestra
    // di completamento chiama `confirm-season` e poi rilegge di qui: il
    // documento fresco porta il flag acceso, il booleano si spegne, e il
    // cancello fa entrare. Senza il ricalcolo la conferma non si vedrebbe e
    // la persona resterebbe chiusa nella finestra che ha appena compilato.
    //
    // ⚠️ E IL DOCUMENTO FRESCO PORTA ANCHE LA RISPOSTA DI FAMIGLIA (rilievo
    // R-1). La cascata del backend scrive `seasonConfirmedFamily` PRIMA di
    // rispondere alla conferma, e questa rilettura viene dopo: lo trova gia'
    // aggiornato. E' il punto in cui la catena si chiude per il GENITORE —
    // finche' un suo figlio non ha confermato il campo vale `false`, il
    // booleano non si spegne, e il cancello lo rimanda alla finestra anche
    // se le tessere sue sono tutte accese.
    const deveCompletareStagione = completamentoStagionaleDaFare(
      contexts,
      stagione,
      backendUser.seasonConfirmedFamily
    );

    // E LO STESSO CARTELLINO DEL RINNOVO, dalla stessa sede e dalle stesse
    // tessere grezze: le due sedi devono consegnare lo stesso oggetto, e dopo
    // una rilettura la riga del rinnovo segue la tessera fresca.
    const statoRinnovo = statoRinnovoDellAnno(backendUser.contexts, stagione);

    // Stessa composizione dell'ingresso, carattere per carattere,
    // e stessa lettura (`GET /users?uid=`): a sessione aperta l'oggetto
    // `user` non cambia forma e nessun campo si perde.
    // ⚠️ Lo spread del documento viene DOPO `firebaseUid` ed `email`: dove il
    // documento porta quei campi vincono i suoi, e i valori dell'utenza
    // Firebase fanno da ripiego. Vale anche quando il documento li porta
    // VUOTI — è così da sempre all'ingresso, quindi non è una novità di
    // questa funzione, ma non va letto come «l'identità viene da Firebase».
    // Lo status arriva dal documento com'è: se nel
    // frattempo qualcuno ha archiviato questa persona, il gate unico
    // (RequireRole) se ne accorgerà al primo controllo — che è la verità del
    // backend, non una disconnessione decisa da qui.
    const utenteAggiornato = {
      firebaseUid: utenzaFirebase.uid,
      email: utenzaFirebase.email,
      ...backendUser,
      contexts: contextsAmmessi,
      // Stessa composizione dell'ingresso, campo derivato compreso: le due
      // sedi devono consegnare lo stesso oggetto, o il gate deciderebbe
      // diversamente prima e dopo un salvataggio.
      deveCompletareStagione,
      statoRinnovo,
    };

    setUser(utenteAggiornato);

    return utenteAggiornato;
  };

  rileggiSchedaRef.current = rileggiScheda;

  // RILETTURA DELLA SOLA STAGIONE, A SESSIONE APERTA (YS-10b).
  //
  // ⚠️ `refreshUserFromBackend` NON copre questo caso, e non e' una svista:
  // quella rilegge la SCHEDA della persona, e la stagione la lascia com'e'.
  // Dopo un comando che cambia la cassettina (apri / chiudi / ritira) l'app
  // continuerebbe percio' a mostrare i valori di prima fino al ricaricamento
  // della pagina. Questa funzione fa l'unica cosa che mancava: richiede
  // `GET /season-config` e riscrive i tre campi della stagione, cosi' che i
  // valori nuovi arrivino a TUTTA l'app e non solo alla pagina che ha dato il
  // comando.
  //
  // ⚠️ NON C'ENTRA CON LO SBLOCCO, e non deve entrarci. La stagione
  // dell'ingresso la legge l'osservatore PRIMA di sbloccare (YS-5bis) e quel
  // percorso resta identico: qui non si aggiunge nessuna attesa. Questa si
  // chiama SOLO a comando — dai pulsanti di Season management — a sessione
  // gia' aperta.
  //
  // Riusa `leggiStagioneDelClub`, quindi ha lo STESSO metro tollerante della
  // lettura d'ingresso: chiuse solo su un `false` esplicito, e un guasto di
  // lettura non si legge mai come una chiusura.
  const refreshSeason = async () => {
    const utenzaFirebase = auth.currentUser;

    // Senza sessione la chiamata partirebbe senza token e il backend
    // risponderebbe 401: non c'e' niente da rileggere.
    if (!utenzaFirebase) return null;

    const lettura = await leggiStagioneDelClub();

    // ⚠️ Stessa guardia di sorpasso delle altre riletture: fra la richiesta e
    // la risposta la persona puo' essere uscita, o esserne entrata un'altra.
    // Un giro sorpassato non scrive nulla.
    if (auth.currentUser?.uid !== utenzaFirebase.uid) return null;

    // ⚠️ SUL GUASTO NON SI SCRIVE NULLA — stessa dottrina di
    // `refreshUserFromBackend`. A sessione aperta «non sono riuscito a
    // chiedere» non e' «non c'e' piu'»: degradare a "error" farebbe sparire
    // dallo schermo due anni che sappiamo ancora buoni, per una linea caduta.
    // All'ingresso il degrado ha senso (non si e' mai saputo nulla), qui no.
    if (lettura?.esito !== "letta") return null;

    const stagione = {
      seasonStatus: "ready",
      operatingSeason: lettura.operatingSeason,
      enrollmentSeason: lettura.enrollmentSeason,
      enrollmentsOpen: lettura.enrollmentsOpen
    };

    setSeason(stagione);

    return stagione;
  };

  // Cancella il messaggio dell'ultimo ingresso non riuscito (RL-8c).
  //
  // La chiama la pagina di accesso quando la persona chiede un nuovo link: da
  // quel momento sta agendo, e tenerle davanti la frase del tentativo di prima
  // le direbbe una cosa vecchia. Nessun altro deve azzerarlo — men che meno
  // l'osservatore nel ramo «nessun utente», v. il commento la' sopra.
  const clearAuthReason = () => setAuthReason(null);

  const logout = async () => {
    await signOut(auth);

    // 🔥 PULIZIA REGISTRAZIONE MULTI
    localStorage.removeItem("userBackend");

    setUser(null);
    setActiveContext(null); // opzionale ma pulito
    setStatus("ready");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        activeContext,        // 👈 AGGIUNGI
        setActiveContext,     // 👈 AGGIUNGI
        status,
        // Perche' l'ingresso non e' riuscito, in codice: lo legge Login.jsx,
        // che ne ricava la frase da mostrare. Sostituisce `error`, che portava
        // la frase gia' scritta e non lo leggeva nessuno.
        authReason,
        clearAuthReason,
        isReady: status === "ready",
        login,        // ✅ ESPORTATA
        logout,
        // Rilettura della propria scheda a sessione aperta (P9): la usano
        // /myprofile (App.jsx) e UserContextManager, che la chiamava già
        // quando ancora non esisteva.
        refreshUserFromBackend,
        // LA STAGIONE DEL CLUB — sede condivisa (YS-5).
        //
        // I due anni di `GET /season-config`, gia' normalizzati a intero, e
        // il loro stato. Sono AGGIUNTI: nulla di ciò che sta sopra cambia
        // nome, posizione o significato, e nessuno li legge ancora — sono la
        // sede che i consumatori futuri troveranno pronta.
        //
        // ⚠️ `seasonStatus` NON è `status`. Quello dice se l'UTENTE è
        // pronto e blocca il disegno dell'app; questo dice solo com'è andata
        // la lettura della stagione. Vale:
        //   "loading"  non ancora saputa (o non ancora chiesta)
        //   "ready"    arrivata: i due anni qui sotto valgono
        //   "error"    `GET /season-config` è fallita, o non ha risposto
        //              entro il tetto: non arriverà
        // I due anni valgono `null` fuori da "ready" — e chi li legge deve
        // guardare `seasonStatus` per sapere quale dei due casi è.
        //
        // ⚠️ DA YS-5bis, su una sessione APERTA questo non vale mai
        // "loading": l'ingresso riuscito aspetta la stagione (col tetto)
        // prima di sbloccare, quindi quando l'app si disegna qui c'è già
        // "ready" o "error". "loading" resta il valore di chi non è dentro —
        // e delle sessioni che non si aprono (ARCHIVED e i rami d'errore, che
        // sbloccano senza aspettare perché non hanno permessi da filtrare).
        //
        // ⚠️ `enrollmentsOpen` (YS-10a) e' il TERZO valore della cassettina:
        // «le iscrizioni sono aperte?». E' AGGIUNTO qui accanto agli anni,
        // e vale solo dentro "ready" come loro — con una differenza: il suo
        // difetto fuori da "ready" e' `true`, non `null`, perche' nel dubbio
        // non si dichiara mai una chiusura. Per ora lo legge SOLO la
        // pastiglia di Season management: nessuna nascita lo consulta
        // ancora, e nessun comando lo cambia (sono YS-10b e YS-10c).
        operatingSeason: season.operatingSeason,
        enrollmentSeason: season.enrollmentSeason,
        seasonStatus: season.seasonStatus,
        enrollmentsOpen: season.enrollmentsOpen,
        // Rilettura della SOLA stagione, a comando (YS-10b). La chiamano i
        // pulsanti di Season management dopo un'azione riuscita: la pagina si
        // aggiorna gia' da sola con la risposta della rotta, ma il resto
        // dell'app legge la stagione DA QUI, e senza questa rilettura
        // resterebbe indietro fino al ricaricamento.
        //
        // ⚠️ AGGIUNTA IN CODA: nulla di cio' che sta sopra cambia nome,
        // posizione o significato.
        refreshSeason,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}